import {
  Autocomplete,
  createFilterOptions,
  TextField,
  Typography,
  AutocompleteRenderOptionState,
} from '@mui/material';
import { theme } from '../../configs/theme';
import { RowBox } from '../../styles/commonStyles';
import ConditionalWrapper from '../wrappers/ConditionalWrapper';
import { TextWrapper, TextWrapperProps } from './TextWrapper';

/**
 * Props for the SearchSelect component.
 *
 * @template T - The type of option objects in the options array
 */
type SearchSelectProps<T = unknown> = {
  /** The currently selected value */
  value?: T | null;
  /** Callback fired when the value changes */
  onChange?: (event: React.ChangeEvent<{ name?: string; value: T | null }>) => void;
  /** Callback fired when the input receives focus */
  onFocus?: () => void;
  /** Callback fired with the typed text when the input loses focus */
  onInputBlur?: (text: string) => void;
  /** Name attribute for the input element */
  name?: string;
  /** Placeholder text displayed when input is empty */
  placeholder?: string;
  /** Array of options to display in the dropdown */
  options?: T[];
  /** Function to get the display label for an option */
  getOptionLabel?: (option: T | string) => string;
  /** Custom render function for dropdown options */
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement> & { key: any },
    option: T | string,
    state: AutocompleteRenderOptionState
  ) => React.ReactNode;
  /** Function to get the search string for an option (used for filtering) */
  getOptionSearchString?: (option: T | string) => string;
  /** Icon to display in the input */
  icon?: React.ReactNode;
  /** Alignment of the icon ('left' or 'right') */
  iconAlign?: 'right' | 'left';
  /** MUI sx prop for styling the Autocomplete component */
  sx?: object;
  /** Custom CSS styles for the input element */
  inputStyle?: React.CSSProperties;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Function to get a unique key for an option */
  getOptionKey?: (option: T | string) => string;
  /** Options this returns true for are shown but cannot be chosen */
  getOptionDisabled?: (option: T | string) => boolean;
  /** Whether to show error state */
  error?: boolean;
  /** Error message text to display */
  errorText?: string;
  /** Grey text shown below the input when there is no error */
  helperText?: string;
  /** Props for the TextWrapper component (if wrapping is needed) */
  wrapperProps?: Omit<TextWrapperProps, 'children'>;
  /**
   * If true, displays a red asterisk (*) next to the label to indicate a required field.
   */
  required?: boolean;
  freeSolo?: boolean;
};

/**
 * A searchable autocomplete select component built on MUI Autocomplete.
 *
 * Provides a text input with dropdown suggestions that can be filtered by typing.
 * Supports custom option rendering, search string generation, and error states.
 * Can work with any type of option object through TypeScript generics.
 *
 * @template T - The type of option objects in the options array
 *
 * @param props - Component props
 *
 * @remarks
 * - Uses `freeSolo` mode, allowing users to type freely
 * - Custom filter logic handles special case for 'product' name prop (includes articleNumber)
 * - Supports custom search strings via `getOptionSearchString` prop
 * - Error state displays red border and error message below input
 * - Can be wrapped with TextWrapper via `wrapperProps`
 */
export const SearchSelect = <T,>({
  value,
  onFocus,
  onInputBlur,
  options = [],
  onChange,
  placeholder,
  inputStyle = { background: theme.palette.background.default },
  icon,
  iconAlign,
  name,
  sx = { width: '100%' },
  getOptionLabel = (option) => (typeof option === 'string' ? option : String(option)),
  renderOption,
  getOptionSearchString,
  getOptionKey = (option) => (typeof option === 'string' ? option : String(option)),
  getOptionDisabled,
  disabled = false,
  error = false,
  errorText,
  helperText,
  required,
  wrapperProps,
  freeSolo = true,
}: SearchSelectProps<T>) => {
  /**
   * Handles changes from the Autocomplete component.
   *
   * Converts the MUI Autocomplete change event into a standard React change event
   * format and calls the parent's onChange handler if provided.
   *
   * @param event - The synthetic event from Autocomplete
   * @param newValue - The newly selected value (or null if cleared)
   */
  const handleAutoCompleteChange = (event: React.SyntheticEvent, newValue: T | string | null) => {
    if (onChange) {
      onChange({
        target: {
          name: name || '',
          value: newValue,
        },
      } as React.ChangeEvent<{ name?: string; value: T | null }>);
    }
  };

  /**
   * Custom filter options configuration for the Autocomplete.
   *
   * Creates a filter function that:
   * - Uses custom search string if `getOptionSearchString` is provided
   * - For 'product' name prop, includes articleNumber in search
   * - Otherwise uses the option label for filtering
   * - Matches from any position in the string
   */
  const customFilterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: T | string) => {
      if (option === null || option === undefined) return '';
      // Use custom search string if provided
      if (getOptionSearchString) {
        return typeof option === 'string' ? option : getOptionSearchString(option);
      }
      //To be able to handle articleNumber as a search parameter along with the label.
      if (name === 'product') {
        return typeof option === 'string'
          ? option //@ts-ignore
          : `${getOptionLabel(option)} ${option.articleNumber}`;
      }
      return typeof option === 'string' ? option : getOptionLabel(option);
    },
  });

  return (
    <ConditionalWrapper
      condition={!!wrapperProps}
      wrapper={(children) => (
        <TextWrapper {...wrapperProps} required={required}>
          {children}
        </TextWrapper>
      )}
    >
      <RowBox
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
        }}
      >
        <Autocomplete
          freeSolo={freeSolo}
          options={options}
          value={value ?? null}
          onFocus={onFocus}
          onBlur={
            onInputBlur &&
            ((event: React.FocusEvent) => {
              // The root also hears blur from its buttons; only the input holds the typed text.
              if (event.target instanceof HTMLInputElement) onInputBlur(event.target.value);
            })
          }
          getOptionLabel={(option) => {
            if (!option) return '';
            return getOptionLabel(option);
          }}
          renderOption={renderOption}
          getOptionKey={getOptionKey}
          getOptionDisabled={getOptionDisabled}
          onChange={handleAutoCompleteChange}
          filterOptions={customFilterOptions}
          sx={{
            ...sx,
            width: '100%',
            border: '1px solid grey',
            borderRadius: '8px',
            backgroundColor: disabled
              ? 'rgba(0, 0, 0, 0.04)'
              : theme.palette.customColors.inputBackground,
            opacity: disabled ? 0.7 : 1,
            '& .MuiInputBase-root': {
              backgroundColor: disabled
                ? 'rgba(0, 0, 0, 0.04)'
                : theme.palette.customColors.inputBackground,
              color: disabled ? 'rgba(0, 0, 0, 0.5)' : 'inherit',
              cursor: disabled ? 'not-allowed' : 'text',
            },
            '& .MuiAutocomplete-endAdornment': {
              opacity: disabled ? 0.5 : 1,
            },
          }}
          disabled={disabled}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              placeholder={placeholder}
              slotProps={{
                input: {
                  ...params.InputProps,
                  disableUnderline: true,
                  startAdornment: icon && iconAlign === 'left' ? icon : null,
                  endAdornment: icon && iconAlign === 'right' ? icon : null,
                  style: {
                    ...inputStyle,
                    borderRadius: '8px',
                    border: 'none',
                    padding: '8px',
                    width: '100%',
                    outline: error ? `1px solid ${theme.palette.status.rejected}` : 'none',
                    backgroundColor: disabled
                      ? 'rgba(0, 0, 0, 0.04)'
                      : theme.palette.customColors.inputBackground,
                  },
                },
              }}
            />
          )}
        />
        {error && (
          <p style={{ color: theme.palette.status.rejected, fontSize: '12px', margin: 0 }}>
            {errorText}
          </p>
        )}
        {!error && helperText && (
          <Typography variant="caption" color="text.secondary">
            {helperText}
          </Typography>
        )}
      </RowBox>
    </ConditionalWrapper>
  );
};
